import { useMemo, useState, type CSSProperties } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import s from "./Library.module.css";
import { CreateDocumentDialog } from "./CreateDocumentDialog";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useDocuments, useDocumentTypes } from "../../api/queries";
import {
  attention,
  ATTENTION_LABEL,
  DOCUMENT_STATUS_LABEL,
  DOCUMENT_STATUSES,
  sortDocuments,
  type Attention,
  type Classification,
  type DocumentRow,
  type DocumentStatus,
} from "../../domain/library";
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  numClass,
  Segmented,
  Select,
  Skeleton,
  Table,
  tableRowLinkClass,
} from "../../design/components";
import { formatDate } from "../../lib/format";
import { t, tn } from "../../i18n";

type Scope = "applicable" | "exclusive";

/**
 * The controlled library as it applies at this site: tenant-wide manuals
 * and procedures plus the site's own certificates, drawings and reports.
 * What a document controller needs to look at — expired, expiring, review
 * overdue or due — sorts to the top.
 */
export function LibraryPage() {
  const { site, serverTime, principal } = useSession();
  const [creating, setCreating] = useState(false);
  const canWrite = hasPermission(principal, PERMISSION.documentsWrite);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const scope = (params.get("scope") as Scope) || "applicable";
  const type = params.get("type") || null;
  const status = (params.get("status") as DocumentStatus | null) || null;
  const q = params.get("q") ?? "";
  const now = useMemo(() => new Date(serverTime), [serverTime]);

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const docs = useDocuments({
    siteScope: scope,
    type: type ?? undefined,
    status: status ?? undefined,
    q: q || undefined,
  });
  const types = useDocumentTypes();
  const typeName = useMemo(() => new Map((types.data ?? []).map((t) => [t.key, t.name])), [types.data]);
  const rows = useMemo(() => sortDocuments(docs.data ?? [], now), [docs.data, now]);
  const counts = useMemo(() => {
    const c: Record<Attention, number> = { expired: 0, expiring: 0, review_overdue: 0, review_due: 0, ok: 0 };
    for (const d of docs.data ?? []) c[attention(d, now)] += 1;
    return c;
  }, [docs.data, now]);

  return (
    <Page
      eyebrow={t("{code} · library", { code: site.code })}
      title="Library"
      actions={
        <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
          <Segmented<Scope>
            ariaLabel="Scope"
            value={scope}
            options={[
              { value: "applicable", label: t("Applies at {code}", { code: site.code }) },
              { value: "exclusive", label: t("{code} only", { code: site.code }) },
            ]}
            onChange={(v) => set("scope", v === "applicable" ? null : v)}
          />
          <Button
            variant="primary"
            onClick={() => setCreating(true)}
            disabled={!canWrite}
            title={
              canWrite
                ? t("Create a controlled document")
                : t("Requires {permission}", { permission: PERMISSION.documentsWrite })
            }
          >
            {t("New document")}
          </Button>
        </span>
      }
    >
      {creating && (
        <CreateDocumentDialog
          onClose={() => setCreating(false)}
          onCreated={(docId) => {
            setCreating(false);
            navigate(`/library/${docId}`);
          }}
        />
      )}
      <div className={s.summary}>
        <Stat label={t("Documents")} value={docs.data?.length ?? "—"} />
        <Stat label={t("Issued")} value={(docs.data ?? []).filter((d) => d.status === "issued").length} />
        <Stat label={t("Expired")} value={counts.expired} colour={counts.expired ? "var(--red)" : undefined} />
        <Stat
          label={t("Review overdue")}
          value={counts.review_overdue}
          colour={counts.review_overdue ? "var(--red)" : undefined}
        />
        <Stat
          label={t("Due within 30 days")}
          value={counts.expiring + counts.review_due}
          colour={counts.expiring + counts.review_due ? "var(--amber)" : undefined}
        />
      </div>

      <div className={s.filters}>
        <Select value={type ?? ""} onChange={(e) => set("type", e.target.value || null)} aria-label={t("Type")}>
          <option value="">{t("Any type")}</option>
          {(types.data ?? []).map((ty) => (
            <option key={ty.key} value={ty.key}>
              {ty.name}
            </option>
          ))}
        </Select>
        <Select value={status ?? ""} onChange={(e) => set("status", e.target.value || null)} aria-label={t("Status")}>
          <option value="">{t("Any status")}</option>
          {DOCUMENT_STATUSES.map((st) => (
            <option key={st} value={st}>
              {t(DOCUMENT_STATUS_LABEL[st])}
            </option>
          ))}
        </Select>
        <Input
          type="search"
          placeholder={t("Search title, reference, tags…")}
          value={q}
          onChange={(e) => set("q", e.target.value || null)}
          aria-label={t("Search")}
        />
        <span className={s.count}>{docs.data ? tn(rows.length, "{n} document", "{n} documents") : ""}</span>
      </div>

      {docs.isPending ? (
        <Skeleton rows={8} height={18} />
      ) : docs.isError ? (
        <ErrorState error={docs.error} retry={() => void docs.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState>
          {scope === "exclusive"
            ? t("No documents match tagged to {code} alone — most controlled documents apply at every site.", {
                code: site.code,
              })
            : t("No documents match.")}
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t("Document")}</th>
              <th>{t("Type")}</th>
              <th>{t("Status")}</th>
              <th>{t("Revision")}</th>
              <th>{t("Issued")}</th>
              <th>{t("Review / expiry")}</th>
              <th>{t("Class")}</th>
              <th style={{ textAlign: "end" }}>{t("Revs")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <Row
                key={d.id}
                d={d}
                now={now}
                typeName={typeName.get(d.type) ?? d.type}
                siteCode={site.code}
                onOpen={() => navigate(`/library/${d.id}`)}
              />
            ))}
          </tbody>
        </Table>
      )}
    </Page>
  );
}

const ATT_COLOUR: Record<Attention, string> = {
  expired: "var(--red)",
  review_overdue: "var(--red)",
  expiring: "var(--amber)",
  review_due: "var(--amber)",
  ok: "var(--muted)",
};

function Row({
  d,
  now,
  typeName,
  siteCode,
  onOpen,
}: {
  d: DocumentRow;
  now: Date;
  typeName: string;
  siteCode: string;
  onOpen: () => void;
}) {
  const att = attention(d, now);
  return (
    <tr
      className={tableRowLinkClass}
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <td className={s.titleCell}>
        <Link to={`/library/${d.id}`} onClick={(e) => e.stopPropagation()}>
          {d.title}
        </Link>
        <div className={s.ref}>
          {d.reference ?? "—"} · <span className={s.scope}>{d.siteId ? siteCode : t("all sites")}</span>
        </div>
        {d.tags.length > 0 && (
          <div className={s.tags}>
            {d.tags.map((tag) => (
              <span key={tag} className={s.tag}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </td>
      <td>{typeName}</td>
      <td>
        <StatusPill status={d.status} />
      </td>
      <td className="mono">{d.revision ?? "—"}</td>
      <td style={{ whiteSpace: "nowrap" }}>{d.issuedOn ? formatDate(d.issuedOn) : "—"}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        {att !== "ok" ? (
          <span className={s.attention} style={{ "--att-c": ATT_COLOUR[att] } as CSSProperties}>
            {t(ATTENTION_LABEL[att])}
          </span>
        ) : (
          <span className="muted">
            {d.expiresOn
              ? t("expires {date}", { date: formatDate(d.expiresOn) })
              : d.reviewDueOn
                ? t("review {date}", { date: formatDate(d.reviewDueOn) })
                : "—"}
          </span>
        )}
      </td>
      <td>
        <ClassPill c={d.classification} />
      </td>
      <td className={numClass}>{d.revisions}</td>
    </tr>
  );
}

const STATUS_COLOUR: Record<DocumentStatus, string> = {
  draft: "var(--dim)",
  in_review: "var(--amber)",
  issued: "var(--green)",
  withdrawn: "var(--dim)",
};

export function StatusPill({ status }: { status: DocumentStatus }) {
  return (
    <span className={s.pill} style={{ "--pill-c": STATUS_COLOUR[status] } as CSSProperties}>
      {t(DOCUMENT_STATUS_LABEL[status])}
    </span>
  );
}

/** The classification as the pill has always written it, lower case. */
const CLASSIFICATION_WORD: Record<Classification, string> = {
  public: "public",
  internal: "internal",
  restricted: "restricted",
};

export function ClassPill({ c }: { c: Classification }) {
  const colour = c === "restricted" ? "var(--red)" : c === "public" ? "var(--blue)" : "var(--dim)";
  return (
    <span
      className={`${s.pill} ${c === "restricted" ? s.pillSolid : ""}`}
      style={{ "--pill-c": colour } as CSSProperties}
    >
      {t(CLASSIFICATION_WORD[c])}
    </span>
  );
}

function Stat({ label, value, colour }: { label: string; value: number | string; colour?: string }) {
  return (
    <div className={s.stat}>
      <div className={s.statValue} style={{ "--stat-c": colour } as CSSProperties}>
        {value}
      </div>
      <div className={s.statLabel}>{label}</div>
    </div>
  );
}
